import { ExternalLink } from "lucide-react";
import { PROVIDER_LABELS } from "@/lib/agent/genieMeetings";
import { MEETING_CONVERSATION_TYPE } from "@/lib/conversation/recordedMeeting";
import { MeetingConversationMetadata } from "@/types/agent/genie-meeting";
import { Conversation } from "@/types/conversation/conversation";
import { getChannelLabel, getConversationChannel } from "../conversationListShared";

interface ConversationFactsStripProps {
  conversation: Conversation;
}

/**
 * Single quiet horizontal strip of facts about this call. Replaces the
 * three stacked card boxes (CompactConversationHeader + MetricsGrid +
 * CustomerDataBlock empty-state) that used to dominate above the fold.
 *
 * No card chrome by design — these are reference values, not headlines.
 * The Summary card immediately below should be the page's first hero.
 */
export function ConversationFactsStrip({ conversation }: ConversationFactsStripProps) {
  const details = conversation?.details;
  const analysis = details?.analysis_result;

  const createdAt = details?.conversation_date || details?.created_at;
  const duration = details?.duration;

  const overallSentiment = analysis?.sentiment_analysis?.overall_sentiment_score;
  const csat = analysis?.sentiment_analysis?.customer_satisfaction_score;

  const formattedDate = formatDateTime(createdAt);
  const formattedDuration = formatDuration(duration);
  const sentimentPct = formatSentiment(overallSentiment);
  const channelLabel = getChannelLabel(getConversationChannel(conversation));

  // A genie meeting says what it was about and where it happened instead of a channel.
  const meeting =
    conversation.type === MEETING_CONVERSATION_TYPE
      ? ((conversation.metadata ?? {}) as MeetingConversationMetadata)
      : null;

  // A call a Listener Genie recorded on its own number says who called.
  const metadata = (conversation.metadata ?? {}) as { source?: string; from?: string | null };
  const caller = metadata.source === "phone" ? metadata.from : null;

  const facts: { label: string; value: string; tone?: "primary" | "secondary"; href?: string }[] = [
    ...(meeting?.title ? [{ label: "Meeting", value: meeting.title }] : []),
    ...(caller ? [{ label: "Caller", value: caller }] : []),
    meeting
      ? {
          label: "Platform",
          value: (meeting.meeting_provider && PROVIDER_LABELS[meeting.meeting_provider]) || "Online meeting",
        }
      : { label: "Channel", value: channelLabel },
    { label: "Date", value: formattedDate },
    { label: "Duration", value: formattedDuration },
    { label: "Sentiment", value: sentimentPct, tone: "primary" },
    {
      label: "Satisfaction",
      value:
        csat !== undefined && csat !== null ? `${csat}%` : "—",
      tone: "secondary",
    },
    ...(meeting?.meeting_link ? [{ label: "Link", value: "Open meeting link", href: meeting.meeting_link }] : []),
  ];

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-1 py-2 mb-6 text-sm">
      {facts.map((fact, idx) => (
        <div key={fact.label} className="flex items-center gap-2 min-w-0">
          <span className="text-xs uppercase tracking-wide text-gray-500 font-medium">
            {fact.label}
          </span>
          {fact.href ? (
            <a
              href={fact.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-semibold text-genie-primary hover:underline"
            >
              {fact.value}
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          ) : (
            <span
              className="font-semibold break-words min-w-0"
              style={{
                color:
                  fact.tone === "primary"
                    ? "#4E9CFF"
                    : fact.tone === "secondary"
                    ? "#FF4DB8"
                    : "#111827",
              }}
            >
              {fact.value}
            </span>
          )}
          {idx < facts.length - 1 && (
            <span className="hidden md:inline text-gray-300 ml-4">·</span>
          )}
        </div>
      ))}
    </div>
  );
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString([], {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function formatSentiment(score: number | null | undefined): string {
  if (score === null || score === undefined) return "—";
  if (score <= 1 && score >= -1) return `${Math.round(score * 100)}%`;
  return `${Math.round(score)}%`;
}
